/**
 * [GF-71] A product matches a promotion when one of the promotion's
 * `ProductFilters` rows (usually a Norce flag) matches the product.
 */
import type { ProductFilters } from "@nwt/norce-pwa/dist/runtime/types/api/shopping/basket";
import type { CatalogPromotion } from "@nwt/norce-pwa/dist/runtime/types/api/shopping/promotion";
import { parseNorceDate } from "../parseNorceDate";

/** A promotion reduced to its text and the rules that pick its products. */
export interface PromotionText {
	id: number;
	text: string;
	filters: PromotionTextFilter[];
}

export interface PromotionTextFilter {
	flagId?: number;
	pricelistId?: number;
	manufacturerId?: number;
	productId?: number;
	partNo?: string;
	categoryIds?: number[];
	typeId?: number;
}

/**
 * Product fields shared by PDP products and listing items.
 *
 * Listing items also carry `PromotionIdSeed` ("internal ids of promotions
 * applied on this product" in Norce's BasketItem docs). It is not used: PDP
 * products don't have it, so card and PDP could disagree, and on cards it is
 * only as fresh as the last Meilisearch reindex.
 */
export interface PromotionTextProduct {
	Id?: number | null;
	PartNo?: string | null;
	FlagIdSeed?: string | null;
	VariantFlagIdSeed?: string | null;
	PriceListId?: number | null;
	ManufacturerId?: number | null;
	Manufacturer?: { Id?: number | null } | null;
	CategoryId?: number | null;
	CategoryIdSeed?: string | null;
	Categories?: { Id?: number | null }[] | null;
	TypeId?: number | null;
}

const positive = (value?: number | null) =>
	typeof value === "number" && value > 0 ? value : undefined;

const parseIdSeed = (seed?: string | null): number[] =>
	(seed ?? "")
		.split(",")
		.map((id) => Number(id.trim()))
		.filter((id) => Number.isInteger(id) && id > 0);

// Norce sends `null` for unset filter fields despite the norce-pwa type.
type NorceFilterRow = {
	[Key in keyof ProductFilters]?: ProductFilters[Key] | null;
};

const toFilter = (raw: NorceFilterRow): PromotionTextFilter | null => {
	const categoryIds = parseIdSeed(raw.CategorySeed);
	const filter: PromotionTextFilter = {
		flagId: positive(raw.FlagId),
		pricelistId: positive(raw.PricelistId),
		manufacturerId: positive(raw.ManufacturerId),
		productId: positive(raw.ProductId) ?? positive(raw.VariantProductId),
		partNo: raw.PartNo?.trim() || undefined,
		categoryIds: categoryIds.length ? categoryIds : undefined,
		typeId: positive(raw.TypeId)
	};
	const isEmpty = Object.values(filter).every((value) => value === undefined);
	// A catalogue-wide row would match every product.
	return isEmpty
		? null
		: (Object.fromEntries(
				Object.entries(filter).filter(([, value]) => value !== undefined)
			) as PromotionTextFilter);
};

const isWithinDates = (promotion: CatalogPromotion, now: Date) => {
	const start = parseNorceDate(promotion.StartDate);
	const end = parseNorceDate(promotion.EndDate);
	if (!Number.isNaN(start) && now.getTime() < start) return false;
	if (!Number.isNaN(end) && now.getTime() > end) return false;
	return true;
};

/** Running promotions that have a text. */
export const toPromotionTexts = (
	promotions: CatalogPromotion[],
	now: Date
): PromotionText[] =>
	promotions.flatMap((promotion) => {
		const text = (promotion.Header || promotion.ShortDescription || "").trim();
		if (!text || !isWithinDates(promotion, now)) return [];
		const filters = (promotion.ProductFilters ?? [])
			.map(toFilter)
			.filter((filter): filter is PromotionTextFilter => filter !== null);
		return filters.length ? [{ id: promotion.Id, text, filters }] : [];
	});

const matchesFilter = (
	filter: PromotionTextFilter,
	product: PromotionTextProduct
) => {
	const flagIds = [
		...parseIdSeed(product.FlagIdSeed),
		...parseIdSeed(product.VariantFlagIdSeed)
	];
	const categoryIds = [
		...parseIdSeed(product.CategoryIdSeed),
		...(product.Categories ?? []).flatMap(({ Id }) => (Id ? [Id] : [])),
		...(product.CategoryId ? [product.CategoryId] : [])
	];
	return (
		(filter.flagId === undefined || flagIds.includes(filter.flagId)) &&
		(filter.pricelistId === undefined ||
			product.PriceListId === filter.pricelistId) &&
		(filter.manufacturerId === undefined ||
			(product.ManufacturerId ?? product.Manufacturer?.Id) ===
				filter.manufacturerId) &&
		(filter.productId === undefined || product.Id === filter.productId) &&
		(filter.partNo === undefined || product.PartNo === filter.partNo) &&
		(filter.typeId === undefined || product.TypeId === filter.typeId) &&
		(filter.categoryIds === undefined ||
			filter.categoryIds.some((id) => categoryIds.includes(id)))
	);
};

/** Texts of the promotions that apply to `product`, in Norce order. */
export const matchPromotionTexts = (
	product: PromotionTextProduct | null | undefined,
	promotions: PromotionText[]
): PromotionText[] => {
	if (!product) return [];
	return promotions.filter((promotion) =>
		promotion.filters.some((filter) => matchesFilter(filter, product))
	);
};
